import React, { useState, useEffect } from 'react';
import {
  Tabs,
  Card,
  Button,
  Table,
  Space,
  Typography,
  Tag,
  Badge,
  Grid,
  Modal,
  message
} from 'antd';
import {
  PlusOutlined,
  DownloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CodeOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { apiRequests } from "src/shared/api/api";
import FeedForm from './FeedForm';
import FeedPreview from './FeedPreview';

const { Text } = Typography;
const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

const Feeds = ({ token }) => {
  const [activeTab, setActiveTab] = useState('feeds');
  const [feeds, setFeeds] = useState([]);
  const [selectedFeed, setSelectedFeed] = useState(null);
  const [showFeedForm, setShowFeedForm] = useState(false);
  const screens = useBreakpoint();

  useEffect(() => {
    loadFeeds();
  }, [token]);

  const loadFeeds = async () => {
    try {
      const response = await apiRequests.feeds.getItems();
      setFeeds(response.data?.feeds || []);
    } catch (error) {
      console.error('Error loading feeds:', error);
      message.error('Ошибка загрузки фидов');
    }
  };

  const handleDeleteFeed = async (id) => {
    Modal.confirm({
      title: 'Удалить фид?',
      content: 'Вы уверены, что хотите удалить этот фид?',
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await apiRequests.feeds.delete(id);
          message.success('Фид успешно удален');
          loadFeeds();
        } catch (error) {
          console.error('Error deleting feed:', error);
          message.error('Ошибка удаления фида');
        }
      },
    });
  };

  const getFeedUrl = (url_token) => {
    const baseURL = window.location.origin.includes('localhost') 
      ? 'https://dev.tablecrm.com/api/v1'
      : window.location.origin + '/api/v1';
    return `${baseURL}/feeds/${url_token}?token=${token}`;
  };

  const feedColumns = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          {record.description && (
            <Text type="secondary" ellipsis style={{ maxWidth: 300 }}>
              {record.description}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      key: 'description',
      render: (text) => (
        <Text type="secondary" ellipsis style={{ maxWidth: 200 }}>
          {text || '—'}
        </Text>
      ),
    },
    {
      title: 'Структура',
      key: 'structure',
      render: (_, record) => (
        <Space>
          <Tag>{record.root_tag}</Tag>
          <Text type="secondary">→</Text>
          <Tag>{record.item_tag}</Tag>
        </Space>
      ),
    },
    {
      title: 'Поля',
      key: 'fields',
      render: (_, record) => (
        <Badge
          count={Object.keys(record.field_tags || {}).length}
          style={{ backgroundColor: '#1890ff' }}
          showZero
        />
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => {
              setSelectedFeed(record);
              setActiveTab('preview');
            }}
          />
          {record.url_token && (
            <Button
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => window.open(getFeedUrl(record.url_token), '_blank')}
            />
          )}
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => {
              setSelectedFeed(record);
              setShowFeedForm(true);
            }}
          />
          <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            onClick={() => handleDeleteFeed(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className='bg-[#f5f5f5] border-color-[#f5f5f5]'>
      {showFeedForm ? (
        <FeedForm
          token={token}
          feed={selectedFeed}
          product={null}
          onCancel={() => {
            setShowFeedForm(false);
            setSelectedFeed(null);
          }}
          onSuccess={() => {
            setShowFeedForm(false);
            setSelectedFeed(null);
            loadFeeds();
            setActiveTab('feeds');
            message.success('Фид успешно сохранен');
          }}
        />
      ) : (
        <Card style={{backgroundColor:"#f5f5f5", borderColor:"#f5f5f5", padding:"0 !inherit"}} bodyStyle={{ padding: 0 }}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            tabBarExtraContent={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedFeed(null);
                  setShowFeedForm(true);
                }}
              >
                Новый фид
              </Button>
            }
          >
            <TabPane
              tab={
                <span>
                  <CodeOutlined />
                  Фиды ({feeds.length})
                </span>
              }
              key="feeds"
            >
              <div>
                <Table
                  columns={feedColumns}
                  dataSource={feeds}
                  rowKey="id"
                  scroll={{ x: screens.xs ? 800 : 'auto' }}
                  locale={{
                    emptyText: 'Фиды не созданы. Создайте первый фид.'
                  }}
                />
                <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
                  Всего фидов: {feeds.length}
                </Text>
              </div>
            </TabPane>

            <TabPane
              tab={
                <span>
                  <FileTextOutlined />
                  Предпросмотр
                </span>
              }
              key="preview"
            >
              <div>
                <FeedPreview feed={selectedFeed} token={token} />
              </div>
            </TabPane>
          </Tabs>
        </Card>
      )}
    </div>
  );
};

export default Feeds;